import { getConstants } from "../../constants/index.js";
/**
 * Fetches a user's position capability ID
 *
 * @param suiClient - SuiClient instance
 * @param network - Network name ("mainnet", "testnet", or "devnet")
 * @param userAddress - Address of the user
 * @returns Promise resolving to the position capability ID or undefined if not found
 */
export const getUserPositionCapId = async (suiClient, network, userAddress) => {
    try {
        const constants = getConstants(network);
        // Fetch owned objects for the user
        const response = await suiClient.getOwnedObjects({
            owner: userAddress,
            options: {
                showContent: true, // Include object content to access fields
            },
            filter: {
                StructType: constants.POSITION_CAP_TYPE,
            },
        });
        if (!response || !response.data || response.data.length === 0) {
            return undefined;
        }
        return response.data[0].data?.objectId;
    }
    catch (error) {
        console.error("Error fetching user positionCap ID:", error);
    }
};
/**
 * Fetches a user's position ID from their position capability
 *
 * @param suiClient - SuiClient instance
 * @param network - Network name ("mainnet", "testnet", or "devnet")
 * @param userAddress - Address of the user
 * @returns Promise resolving to the position ID or undefined if not found
 */
export const getUserPositionId = async (suiClient, network, userAddress) => {
    try {
        const constants = getConstants(network);
        // Fetch owned objects for the user
        const response = await suiClient.getOwnedObjects({
            owner: userAddress,
            options: {
                showContent: true, // Include object content to access fields
            },
            filter: {
                StructType: constants.POSITION_CAP_TYPE,
            },
        });
        if (!response || !response.data || response.data.length === 0) {
            return undefined;
        }
        // Find the first PositionCap object and extract the positionCap ID
        const positionCapObject = response.data[0]
            .data;
        return positionCapObject.content.fields.position_id;
    }
    catch (error) {
        console.error("Error fetching user position ID:", error);
    }
};
/**
 * Fetches a user's position IDs from their position capabilities
 *
 * @param suiClient - SuiClient instance
 * @param network - Network name ("mainnet", "testnet", or "devnet")
 * @param userAddress - Address of the user
 * @returns Promise resolving to the position IDs, empty array if not found, undefined if error
 */
export const getUserPositionIds = async (suiClient, network, userAddress) => {
    try {
        const constants = getConstants(network);
        // Fetch owned objects for the user
        const response = await suiClient.getOwnedObjects({
            owner: userAddress,
            options: {
                showContent: true, // Include object content to access fields
            },
            filter: {
                StructType: constants.POSITION_CAP_TYPE,
            },
        });
        if (!response || !response.data || response.data.length === 0) {
            return [];
        }
        // Find all the PositionCap objects and extract the positionCap IDs
        const positionCapObjects = response.data.map((obj) => obj.data);
        const ids = positionCapObjects.map((obj) => obj.content.fields.position_id);
        return ids;
    }
    catch (error) {
        console.error("Error fetching user position IDs:", error);
    }
};
/**
 * Retrieves the complete position object for a user
 *
 * @param suiClient - SuiClient instance
 * @param network - Network name ("mainnet", "testnet", or "devnet")
 * @param userAddress - Address of the user
 * @returns Promise resolving to the position object or undefined if not found
 */
export const getUserPosition = async (suiClient, network, userAddress) => {
    const constants = getConstants(network);
    const positionId = await getUserPositionId(suiClient, network, userAddress);
    if (!positionId) {
        console.error("No position ID found");
        return undefined;
    }
    const response = await suiClient.getDynamicFieldObject({
        parentId: constants.POSITION_TABLE_ID,
        name: {
            type: "0x2::object::ID",
            value: positionId,
        },
    });
    return response.data;
};
//# sourceMappingURL=functions.js.map